import { useState, useEffect, useCallback, useRef } from 'react';
import { vendorInvoicesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload, FileText, Search, CheckCircle2, AlertTriangle, XCircle,
  ChevronLeft, Loader2, Eye, Trash2, Link2, HelpCircle, Ban, RefreshCw,
} from 'lucide-react';

const STATUS_CFG = {
  uploaded: { label: 'Uploaded', color: '#6B7280', bg: 'rgba(107,114,128,0.08)' },
  processing: { label: 'Processing', color: '#2563EB', bg: 'rgba(37,99,235,0.08)' },
  partially_matched: { label: 'Partial Match', color: '#D97706', bg: 'rgba(245,158,11,0.08)' },
  fully_matched: { label: 'Fully Matched', color: '#059669', bg: 'rgba(16,185,129,0.08)' },
  disputed: { label: 'Disputed', color: '#DC2626', bg: 'rgba(239,68,68,0.08)' },
};

const CONFIDENCE_CFG = {
  auto_high: { label: 'High', color: '#059669', icon: CheckCircle2 },
  auto_low: { label: 'Low', color: '#D97706', icon: AlertTriangle },
  manual: { label: 'Manual', color: '#2563EB', icon: Link2 },
  unmatched: { label: 'No Match', color: '#DC2626', icon: XCircle },
};

/* ========== Upload Form ========== */
function UploadForm({ onUploaded }) {
  const [vendorName, setVendorName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !vendorName.trim() || !invoiceNumber.trim()) {
      toast.error('Vendor name, invoice number, and file are required');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('vendor_name', vendorName.trim());
      fd.append('invoice_number', invoiceNumber.trim());
      if (invoiceDate) fd.append('invoice_date', invoiceDate);
      const res = await vendorInvoicesAPI.upload(fd);
      toast.success('Invoice uploaded');
      onUploaded(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  return (
    <Card className="rounded-xl border-2 bg-white" style={{ borderColor: 'rgba(197,165,90,0.4)' }} data-testid="upload-form">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
          <Upload className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} /> Upload Vendor Invoice
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Vendor Name *</Label>
            <Input value={vendorName} onChange={e => setVendorName(e.target.value)}
              placeholder="e.g. Italian Textiles Mumbai" className="mt-1 h-9" data-testid="vendor-name-input" />
          </div>
          <div>
            <Label className="text-xs">Invoice Number *</Label>
            <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)}
              placeholder="e.g. INV-2026-045" className="mt-1 h-9" data-testid="invoice-number-input" />
          </div>
          <div>
            <Label className="text-xs">Invoice Date</Label>
            <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
              className="mt-1 h-9" data-testid="invoice-date-input" />
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">File (PDF/Image/Excel) *</Label>
              <Input type="file" ref={fileRef}
                accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="mt-1 h-9 text-xs" data-testid="invoice-file-input" />
            </div>
            <Button type="submit" disabled={uploading} className="h-9 gap-1.5"
              style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="upload-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/* ========== Extraction Review ========== */
function ExtractionReview({ invoice, onSaved, onBack }) {
  const [items, setItems] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [method, setMethod] = useState('');

  const doExtract = async () => {
    setExtracting(true);
    try {
      const res = await vendorInvoicesAPI.extract(invoice.id);
      setItems(res.data.items || []);
      setMethod(res.data.extraction_method || '');
      setExtracted(true);
      if (res.data.count === 0) {
        toast.info('No items extracted. Add them manually below.');
      } else {
        toast.success(`${res.data.count} line items extracted`);
      }
    } catch (err) {
      toast.error('Extraction failed. Add items manually.');
      setExtracted(true);
    }
    setExtracting(false);
  };

  useEffect(() => { doExtract(); }, []);

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const addItem = () => {
    setItems(prev => [...prev, { line_number: prev.length + 1, description_raw: '', quantity: null, rate: null, amount: null }]);
  };

  const handleSave = async () => {
    const valid = items.filter(i => i.description_raw?.trim());
    if (!valid.length) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      const res = await vendorInvoicesAPI.saveLineItems(invoice.id, { line_items: valid.map((it, i) => ({ ...it, line_number: i + 1 })) });
      const matchResult = res.data?.match_result;
      if (matchResult) {
        const { matched, unmatched } = matchResult;
        if (matched > 0 && unmatched === 0) {
          toast.success(`All ${matched} items auto-matched!`);
        } else if (matched > 0) {
          toast.success(`Auto-matched ${matched} items, ${unmatched} need review`);
        } else {
          toast.info('Line items saved. No auto-matches found — use manual matching.');
        }
      } else {
        toast.success('Line items saved');
      }
      onSaved();
    } catch (err) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="extraction-review">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1" data-testid="back-to-list">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>
              Review Extracted Items — {invoice.invoice_number}
            </h2>
            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
              {invoice.vendor_name} &middot; {method === 'excel_parse' ? 'Parsed from Excel' : 'Extracted via AI'} &middot; {items.length} items
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addItem} className="gap-1 h-8" data-testid="add-line-btn">
            + Add Row
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1 h-8"
            style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="save-items-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Save & Match
          </Button>
        </div>
      </div>

      {extracting ? (
        <Card className="rounded-xl bg-white border">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: 'var(--brand-gold)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
              {invoice.file_type === 'excel' ? 'Parsing spreadsheet...' : 'AI is reading the invoice...'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-xl bg-white border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="extraction-table">
              <thead>
                <tr className="border-b" style={{ backgroundColor: 'rgba(27,42,74,0.02)', borderColor: 'rgba(27,42,74,0.08)' }}>
                  <th className="text-left px-3 py-2 font-semibold w-8" style={{ color: 'var(--ink-muted)' }}>#</th>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: 'var(--ink-muted)' }}>Description</th>
                  <th className="text-right px-3 py-2 font-semibold w-24" style={{ color: 'var(--ink-muted)' }}>Qty</th>
                  <th className="text-right px-3 py-2 font-semibold w-24" style={{ color: 'var(--ink-muted)' }}>Rate</th>
                  <th className="text-right px-3 py-2 font-semibold w-28" style={{ color: 'var(--ink-muted)' }}>Amount</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50/50" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                    <td className="px-3 py-1.5 tabular-nums" style={{ color: 'var(--ink-muted)' }}>{idx + 1}</td>
                    <td className="px-3 py-1.5">
                      <Input value={item.description_raw || ''} onChange={e => updateItem(idx, 'description_raw', e.target.value)}
                        className="h-7 text-xs border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:bg-white focus-visible:px-2 focus-visible:border"
                        placeholder="Fabric description..." data-testid={`line-desc-${idx}`} />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input type="number" step="0.01" value={item.quantity ?? ''} onChange={e => updateItem(idx, 'quantity', e.target.value ? parseFloat(e.target.value) : null)}
                        className="h-7 text-xs text-right border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:bg-white focus-visible:px-2 focus-visible:border w-full" data-testid={`line-qty-${idx}`} />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input type="number" step="0.01" value={item.rate ?? ''} onChange={e => updateItem(idx, 'rate', e.target.value ? parseFloat(e.target.value) : null)}
                        className="h-7 text-xs text-right border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:bg-white focus-visible:px-2 focus-visible:border w-full" data-testid={`line-rate-${idx}`} />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input type="number" step="0.01" value={item.amount ?? ''} onChange={e => updateItem(idx, 'amount', e.target.value ? parseFloat(e.target.value) : null)}
                        className="h-7 text-xs text-right border-0 bg-transparent px-0 focus-visible:ring-0 focus-visible:bg-white focus-visible:px-2 focus-visible:border w-full" data-testid={`line-amt-${idx}`} />
                    </td>
                    <td className="px-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeItem(idx)}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8" style={{ color: 'var(--ink-muted)' }}>
                    No items extracted. Click "Add Row" to enter manually.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ========== Reconciliation Dashboard ========== */
function ReconciliationView({ invoice, onBack, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [searchModal, setSearchModal] = useState(null); // { lineItem }
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await vendorInvoicesAPI.get(invoice.id);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load invoice');
    }
    setLoading(false);
  }, [invoice.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runMatch = async () => {
    setMatching(true);
    try {
      const res = await vendorInvoicesAPI.runMatching(invoice.id);
      toast.success(`Matched: ${res.data.matched}, Unmatched: ${res.data.unmatched}`);
      fetchData();
    } catch (err) {
      toast.error('Matching failed');
    }
    setMatching(false);
  };

  const confirmLine = async (lineId) => {
    try {
      await vendorInvoicesAPI.confirmMatch(invoice.id, lineId, {});
      toast.success('Match confirmed');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const ignoreLine = async (lineId) => {
    try {
      await vendorInvoicesAPI.ignoreLine(invoice.id, lineId);
      toast.success('Line ignored');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const disputeLine = async (lineId) => {
    try {
      await vendorInvoicesAPI.disputeLine(invoice.id, lineId);
      toast.success('Line disputed');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const openSearch = (lineItem) => {
    setSearchModal(lineItem);
    setSearchQuery(lineItem.description_raw || '');
    setSearchResults([]);
  };

  const doSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await vendorInvoicesAPI.searchFabrics(searchQuery.trim());
      setSearchResults(res.data || []);
    } catch { toast.error('Search failed'); }
    setSearching(false);
  };

  const selectFabricMatch = async (fabric, poLine) => {
    if (!searchModal) return;
    try {
      await vendorInvoicesAPI.changeMatch(invoice.id, searchModal.id, {
        matched_fabric_id: fabric.id,
        matched_po_line_item_id: poLine?.id || null,
        matched_order_item_id: poLine?.order_item_id || null,
      });
      toast.success('Match updated');
      setSearchModal(null);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-10 w-48" /><Skeleton className="h-96" /></div>;
  if (!data) return null;

  const items = data.line_items || [];
  const sc = STATUS_CFG[data.status] || STATUS_CFG.uploaded;
  const matchedCount = items.filter(i => i.match_status === 'matched').length;
  const pendingCount = items.filter(i => i.match_status === 'pending').length;

  return (
    <div className="space-y-4" data-testid="reconciliation-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1" data-testid="back-to-list-recon">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>{data.invoice_number}</h2>
              <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-semibold" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</Badge>
            </div>
            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
              {data.vendor_name} &middot; {data.invoice_date} &middot; {items.length} lines &middot; {matchedCount} matched, {pendingCount} pending
              {data.total_amount != null && <> &middot; <span className="font-semibold">&#8377;{data.total_amount?.toLocaleString('en-IN')}</span></>}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-1 h-8" data-testid="refresh-recon">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
          <Button size="sm" onClick={runMatch} disabled={matching} className="gap-1 h-8"
            style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="run-match-btn">
            {matching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
            {matching ? 'Matching...' : 'Run Auto-Match'}
          </Button>
        </div>
      </div>

      {/* Reconciliation Table */}
      <Card className="rounded-xl bg-white border overflow-hidden shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="reconciliation-table">
            <thead>
              <tr style={{ backgroundColor: 'rgba(27,42,74,0.03)', borderBottom: '1px solid rgba(27,42,74,0.08)' }}>
                <th className="text-left px-3 py-2.5 font-semibold w-8" style={{ color: 'var(--ink-muted)' }}>#</th>
                <th className="text-left px-3 py-2.5 font-semibold min-w-[180px]" style={{ color: 'var(--ink-muted)' }}>Invoice Description</th>
                <th className="text-left px-3 py-2.5 font-semibold min-w-[160px]" style={{ color: 'var(--ink-muted)' }}>Matched Fabric</th>
                <th className="text-center px-3 py-2.5 font-semibold w-20" style={{ color: 'var(--ink-muted)' }}>Confidence</th>
                <th className="text-left px-3 py-2.5 font-semibold min-w-[100px]" style={{ color: 'var(--ink-muted)' }}>PO / Order</th>
                <th className="text-right px-3 py-2.5 font-semibold w-20" style={{ color: 'var(--ink-muted)' }}>Inv Qty</th>
                <th className="text-right px-3 py-2.5 font-semibold w-20" style={{ color: 'var(--ink-muted)' }}>PO Qty</th>
                <th className="text-right px-3 py-2.5 font-semibold w-20" style={{ color: 'var(--ink-muted)' }}>Inv Rate</th>
                <th className="text-right px-3 py-2.5 font-semibold w-20" style={{ color: 'var(--ink-muted)' }}>PO Rate</th>
                <th className="text-right px-3 py-2.5 font-semibold w-24" style={{ color: 'var(--ink-muted)' }}>Amount</th>
                <th className="text-center px-2 py-2.5 font-semibold w-28" style={{ color: 'var(--ink-muted)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const conf = CONFIDENCE_CFG[item.match_confidence] || CONFIDENCE_CFG.unmatched;
                const ConfIcon = conf.icon;
                const qtyMismatch = item.quantity && item.po_quantity && Math.abs(item.quantity - item.po_quantity) > 0.01;
                const rateMismatch = item.rate && item.po_rate && Math.abs(item.rate - item.po_rate) > 0.01;
                const isIgnored = item.match_status === 'ignored';
                const isDisputed = item.match_status === 'disputed';

                return (
                  <tr key={item.id}
                    className={`border-b hover:bg-gray-50/50 ${isIgnored ? 'opacity-40' : ''}`}
                    style={{ borderColor: 'rgba(27,42,74,0.04)' }}
                    data-testid={`recon-row-${item.id}`}>
                    <td className="px-3 py-2 tabular-nums" style={{ color: 'var(--ink-muted)' }}>{item.line_number}</td>
                    <td className="px-3 py-2">
                      <p className="font-medium truncate max-w-[220px]" style={{ color: 'var(--navy-ink)' }}>{item.description_raw}</p>
                      {item.matched_alias_text && item.matched_alias_text !== item.description_raw && (
                        <p className="text-[9px] truncate" style={{ color: 'var(--ink-muted)' }}>via: {item.matched_alias_text}</p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {item.fabric_info ? (
                        <div>
                          <p className="font-medium" style={{ color: 'var(--brand-navy)' }}>{item.fabric_info.internal_code}</p>
                          <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>{item.fabric_info.mill_name} {item.fabric_info.colour_primary ? `· ${item.fabric_info.colour_primary}` : ''}</p>
                        </div>
                      ) : (
                        <span style={{ color: '#DC2626' }}>—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <ConfIcon className="w-3 h-3" style={{ color: conf.color }} />
                        <span className="font-semibold" style={{ color: conf.color }}>{conf.label}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {item.matched_order_number ? (
                        <span className="font-medium" style={{ color: 'var(--brand-navy)' }}>{item.matched_order_number}</span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{item.quantity ?? '—'}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-medium ${qtyMismatch ? 'text-red-600' : ''}`}>
                      {item.po_quantity ?? '—'}
                      {qtyMismatch && <AlertTriangle className="w-2.5 h-2.5 inline ml-0.5 text-red-500" />}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{item.rate != null ? `₹${item.rate.toLocaleString('en-IN')}` : '—'}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-medium ${rateMismatch ? 'text-red-600' : ''}`}>
                      {item.po_rate != null ? `₹${item.po_rate.toLocaleString('en-IN')}` : '—'}
                      {rateMismatch && <AlertTriangle className="w-2.5 h-2.5 inline ml-0.5 text-red-500" />}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--navy-ink)' }}>
                      {item.amount != null ? `₹${item.amount.toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className="px-2 py-2">
                      {!isIgnored && !isDisputed && (
                        <div className="flex items-center justify-center gap-0.5">
                          {item.match_status === 'matched' && item.match_confidence !== 'manual' && (
                            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[9px] gap-0.5" style={{ color: '#059669' }}
                              onClick={() => confirmLine(item.id)} data-testid={`confirm-${item.id}`}>
                              <CheckCircle2 className="w-2.5 h-2.5" /> OK
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[9px] gap-0.5"
                            onClick={() => openSearch(item)} data-testid={`change-${item.id}`}>
                            <Search className="w-2.5 h-2.5" /> Match
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 px-1 text-[9px]" style={{ color: '#6B7280' }}
                            onClick={() => ignoreLine(item.id)} data-testid={`ignore-${item.id}`}>
                            <Ban className="w-2.5 h-2.5" />
                          </Button>
                        </div>
                      )}
                      {isIgnored && <span className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Ignored</span>}
                      {isDisputed && <Badge className="text-[8px] px-1" style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#DC2626' }}>Disputed</Badge>}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={11} className="text-center py-12" style={{ color: 'var(--ink-muted)' }}>
                  No line items. Go back and extract items first.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Search & Manual Match Dialog */}
      <Dialog open={!!searchModal} onOpenChange={() => setSearchModal(null)}>
        <DialogContent className="max-w-lg" data-testid="search-match-modal">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>Find Fabric Match</DialogTitle>
          </DialogHeader>
          <p className="text-xs mb-2" style={{ color: 'var(--ink-muted)' }}>
            Searching for: <strong>{searchModal?.description_raw}</strong>
          </p>
          <div className="flex gap-2">
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              placeholder="Search fabric code, vendor code, mill..."
              className="h-9 text-xs flex-1" data-testid="fabric-search-input" />
            <Button size="sm" onClick={doSearch} disabled={searching} className="h-9 gap-1"
              style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="fabric-search-btn">
              {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              Search
            </Button>
          </div>
          <div className="max-h-[300px] overflow-y-auto space-y-2 mt-2">
            {searchResults.map(fabric => (
              <div key={fabric.id} className="p-2.5 rounded-lg border hover:border-[color:var(--brand-gold)] cursor-pointer"
                style={{ borderColor: 'rgba(27,42,74,0.08)', transition: 'border-color 150ms' }}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-xs font-semibold" style={{ color: 'var(--brand-navy)' }}>{fabric.internal_code}</p>
                    <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                      {fabric.mill_name} {fabric.vendor_code ? `· ${fabric.vendor_code}` : ''} {fabric.colour_primary ? `· ${fabric.colour_primary}` : ''}
                    </p>
                  </div>
                  {(!fabric.pending_po_lines || fabric.pending_po_lines.length === 0) && (
                    <Button size="sm" className="h-6 text-[9px] px-2" style={{ backgroundColor: 'var(--brand-navy)' }}
                      onClick={() => selectFabricMatch(fabric, null)} data-testid={`select-fabric-${fabric.id}`}>
                      Select
                    </Button>
                  )}
                </div>
                {(fabric.pending_po_lines || []).map(po => (
                  <div key={po.id} className="flex items-center justify-between mt-1 pl-3 py-1 rounded"
                    style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
                    <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                      PO: {po.order_number} · {po.quantity_metres}m · ₹{po.rate_per_metre}/m
                    </span>
                    <Button size="sm" className="h-5 text-[9px] px-2 gap-0.5" style={{ backgroundColor: '#059669' }}
                      onClick={() => selectFabricMatch(fabric, po)} data-testid={`select-po-${po.id}`}>
                      <Link2 className="w-2 h-2" /> Match PO
                    </Button>
                  </div>
                ))}
              </div>
            ))}
            {searchResults.length === 0 && !searching && searchQuery && (
              <p className="text-center py-4 text-xs" style={{ color: 'var(--ink-muted)' }}>No results. Try a different search.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ========== Invoice List ========== */
function InvoiceList({ invoices, loading, onSelect }) {
  if (loading) return (
    <Card className="rounded-xl bg-white border"><CardContent className="p-0">
      {[1,2,3].map(i => <div key={i} className="px-4 py-3 border-b"><Skeleton className="h-6 w-full" /></div>)}
    </CardContent></Card>
  );

  if (!invoices.length) return (
    <Card className="rounded-xl bg-white border">
      <CardContent className="py-16 text-center">
        <FileText className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: 'var(--brand-navy)' }} />
        <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>No vendor invoices yet</p>
        <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>Upload an invoice above to get started</p>
      </CardContent>
    </Card>
  );

  return (
    <Card className="rounded-xl bg-white border overflow-hidden shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]">
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="invoices-table">
          <thead>
            <tr style={{ backgroundColor: 'rgba(27,42,74,0.02)', borderBottom: '1px solid rgba(27,42,74,0.08)' }}>
              {['Invoice #', 'Vendor', 'Date', 'Amount', 'Lines', 'Matched', 'Status', ''].map(h => (
                <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => {
              const sc = STATUS_CFG[inv.status] || STATUS_CFG.uploaded;
              return (
                <tr key={inv.id} className="border-b hover:bg-gray-50/50 cursor-pointer group"
                  style={{ borderColor: 'rgba(27,42,74,0.04)' }}
                  onClick={() => onSelect(inv)}
                  data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-2.5 font-semibold" style={{ color: 'var(--brand-navy)' }}>{inv.invoice_number}</td>
                  <td className="px-4 py-2.5" style={{ color: 'var(--navy-ink)' }}>{inv.vendor_name}</td>
                  <td className="px-4 py-2.5" style={{ color: 'var(--ink-muted)' }}>{inv.invoice_date}</td>
                  <td className="px-4 py-2.5 font-semibold tabular-nums" style={{ color: 'var(--navy-ink)' }}>
                    {inv.total_amount != null ? `₹${inv.total_amount.toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums" style={{ color: 'var(--ink-muted)' }}>{inv.line_count || 0}</td>
                  <td className="px-4 py-2.5 tabular-nums">
                    <span style={{ color: '#059669' }}>{inv.matched_count || 0}</span>
                    {inv.pending_count > 0 && <span style={{ color: '#D97706' }}> / {inv.pending_count} pending</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 font-semibold" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <Eye className="w-3.5 h-3.5 opacity-0 group-hover:opacity-40" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ========== Main Page ========== */
export default function VendorInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | extract | recon
  const [activeInvoice, setActiveInvoice] = useState(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorInvoicesAPI.list();
      setInvoices(res.data || []);
    } catch (err) {
      console.error('Failed to fetch invoices', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleUploaded = (inv) => {
    setActiveInvoice(inv);
    setView('extract');
  };

  const handleExtractionSaved = () => {
    setView('recon');
    fetchInvoices();
  };

  const handleSelect = (inv) => {
    setActiveInvoice(inv);
    if (inv.status === 'uploaded') {
      setView('extract');
    } else {
      setView('recon');
    }
  };

  const goToList = () => {
    setView('list');
    setActiveInvoice(null);
    fetchInvoices();
  };

  return (
    <div className="space-y-6" data-testid="vendor-invoices-page">
      {view === 'list' && (
        <>
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Vendor Invoices</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>Upload, extract & reconcile vendor invoices against POs</p>
          </div>
          <UploadForm onUploaded={handleUploaded} />
          <InvoiceList invoices={invoices} loading={loading} onSelect={handleSelect} />
        </>
      )}

      {view === 'extract' && activeInvoice && (
        <ExtractionReview invoice={activeInvoice} onSaved={handleExtractionSaved} onBack={goToList} />
      )}

      {view === 'recon' && activeInvoice && (
        <ReconciliationView invoice={activeInvoice} onBack={goToList} onRefresh={fetchInvoices} />
      )}
    </div>
  );
}
